import * as React from 'react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import companies from 'modules/core/store/collections/companies'

import { IncidenceQueryData } from 'modules/bookkeepers/hooks/useGetIncidences'
import ChangesCollection from 'modules/bookkeepers/store/collections/changes'
import compensationChangesCollection from 'modules/bookkeepers/store/collections/compensationChanges'
import StatusesCollection from 'modules/bookkeepers/store/collections/statuses'
import taxonomies from 'modules/contracts/store/collections/taxonomies'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import type { IncidenceChangeItem } from 'modules/payroll_bundle/lib/types'
import buildCompensationChanges from 'modules/payroll_bundle/pages/IncidenceHistoryPage/helpers/buildCompensationChanges'
// TODO: consider moving these to the lib folder within the module
import buildIncidenceChanges from 'modules/payroll_bundle/pages/IncidenceHistoryPage/helpers/buildIncidenceChanges'
import buildStatusChanges from 'modules/payroll_bundle/pages/IncidenceHistoryPage/helpers/buildStatusChanges'
import buildSupplementChanges from 'modules/payroll_bundle/pages/IncidenceHistoryPage/helpers/buildSupplementChanges'
import { sortChanges } from 'modules/payroll_bundle/pages/IncidenceHistoryPage/helpers/changesHelper'
import supplementObservations from 'modules/payroll_bundle/store/collections/supplementObservations'
import { Supplements } from 'modules/payroll_bundle/store/collections/supplements'

interface CompensationHistoryParams {
  data: {
    bookkeeper_view: boolean
    employee_ids?: string[]
    contract_version_id?: string
    include_previous_compensations?: boolean
  }
}

const useFetchIncidenceChangeItems = (
  incidenceId: string,
  isBookkeeperView: boolean,
  incidenceType: string | null,
  incidenceTargetId: string | null,
  incidence: IncidenceQueryData
) => {
  const [changeItems, setChangeItems] = React.useState<IncidenceChangeItem[]>([])
  const company = companies.current

  const shouldShowCompensationChanges = [
    INCIDENCE_TYPES.contract,
    INCIDENCE_TYPES.hiring,
    INCIDENCE_TYPES.termination,
  ].includes(incidenceType ?? '')

  const { loading } = useAsyncData(async () => {
    const isContractType = [INCIDENCE_TYPES.contract].includes(incidenceType ?? '')
    const isHiringType = incidenceType === INCIDENCE_TYPES.hiring
    const isTerminationType = incidenceType === INCIDENCE_TYPES.termination
    const incidenceChangesCollection = new ChangesCollection([], incidenceId)

    const incidenceStatusesCollection = new StatusesCollection([], incidenceId)
    const supplementsCollection = new Supplements([])
    const params: CompensationHistoryParams = {
      data: {
        bookkeeper_view: isBookkeeperView,
      },
    }

    let fetchCalls = [
      incidenceChangesCollection.fetch(params),
      incidenceStatusesCollection.fetch(params),
    ]

    if (incidenceTargetId && shouldShowCompensationChanges) {
      if (isContractType) {
        params.data.contract_version_id = incidenceTargetId
        params.data.include_previous_compensations = true
      }

      if (isHiringType || isTerminationType) params.data.employee_ids = [incidenceTargetId]

      fetchCalls = [...fetchCalls, compensationChangesCollection.fetch(params)]
    }

    if (isTerminationType) {
      fetchCalls = [
        ...fetchCalls,
        supplementsCollection.fetch({
          data: {
            employee_ids: [incidenceTargetId],
            bookkeeper_view: isBookkeeperView,
          },
        }),
        supplementObservations.fetch({
          data: {
            supplement_ids: supplementsCollection.map((s) => s.id),
            bookkeeper_view: isBookkeeperView,
          },
        }),
        taxonomies.fetch({ data: { bookkeeper_view: isBookkeeperView } }),
      ]
    }

    await Promise.all(fetchCalls)

    const incidenceChanges = buildIncidenceChanges(
      incidenceChangesCollection.models,
      incidenceType ?? '',
      company,
      incidenceTargetId,
      incidence
    )

    const statusChanges = buildStatusChanges(incidenceStatusesCollection.models)

    const compensationChanges = shouldShowCompensationChanges
      ? buildCompensationChanges(incidenceTargetId, incidenceType, compensationChangesCollection)
      : []

    const supplementChanges: IncidenceChangeItem[] = isTerminationType
      ? buildSupplementChanges(supplementsCollection, company)
      : []

    const sortedChangeItems = [
      ...incidenceChanges,
      ...statusChanges,
      ...compensationChanges,
      ...supplementChanges,
    ].sort(sortChanges)
    setChangeItems(sortedChangeItems)
  }, [
    company.id,
    isBookkeeperView,
    incidenceId,
    incidenceType,
    shouldShowCompensationChanges,
    incidenceTargetId,
  ])

  return {
    loading,
    changeItems,
  }
}

export default useFetchIncidenceChangeItems
